import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Copy, ExternalLink, Check, X, CalendarClock, MessageCircle, Wand2 } from "lucide-react";

export default function Asistente() {
  const [cfg, setCfg] = useState(null);
  const [url, setUrl] = useState(null);
  const [resv, setResv] = useState([]);
  const [acts, setActs] = useState([]);
  const [saving, setSaving] = useState(false);
  const [autoUrl, setAutoUrl] = useState("");
  const [autofilling, setAutofilling] = useState(false);

  const loadCfg = () => api.get("/assistant/config").then((r) => { setCfg(r.data.config || {}); setUrl(r.data.public_url); });
  const loadResv = () => api.get("/reservations").then((r) => setResv(r.data.reservations || []));
  const loadActs = () => api.get("/actions").then((r) => setActs(r.data.actions || [])).catch(() => {});
  useEffect(() => { loadCfg(); loadResv(); loadActs(); }, []);

  const save = async (extra = {}) => {
    setSaving(true);
    try {
      const { data } = await api.put("/assistant/config", { ...cfg, ...extra });
      setCfg(data.config); setUrl(data.public_url);
      toast.success("Guardado");
    } catch (e) { toast.error(e.response?.data?.detail || "Error al guardar"); }
    finally { setSaving(false); }
  };

  const autofill = async () => {
    if (!autoUrl.trim()) return toast.error("Pon la dirección de tu web");
    setAutofilling(true);
    try {
      const { data } = await api.post("/assistant/autofill", { url: autoUrl });
      await save({
        business_name: data.business_name || cfg.business_name,
        welcome: data.welcome || cfg.welcome,
        descripcion: data.descripcion || cfg.descripcion,
        servicios: data.servicios || cfg.servicios,
        horarios: data.horarios || cfg.horarios,
      });
      toast.success("¡Listo! Rellené tu asistente desde tu web. Revísalo abajo.");
    } catch (e) { toast.error(e.response?.data?.detail || "No pude leer la web"); }
    finally { setAutofilling(false); }
  };

  const setStatus = async (id, status) => {
    await api.patch(`/reservations/${id}`, { status });
    loadResv();
  };

  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copiado"); };
  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  if (!cfg) return <div className="p-8 text-[var(--text-dim)]">Cargando…</div>;

  const active = !!cfg.slug && cfg.enabled !== false;
  const embed = url ? `<iframe src="${url}" style="border:0;width:100%;height:640px"></iframe>` : "";

  return (
    <div className="p-8 space-y-6" data-testid="asistente-page">
      <div className="border-b border-[var(--border)] pb-5">
        <div className="label-mono mb-2">ATENCIÓN AL CLIENTE 24/7</div>
        <h1 className="font-display font-black text-4xl tracking-tight">Asistente para tus clientes</h1>
        <p className="text-sm text-[var(--text-dim)] mt-1">
          Un asistente que responde a TUS clientes y coge reservas/citas, 24 horas. Compártelo o incrústalo en tu web.
        </p>
      </div>

      {!cfg.slug ? (
        <div className="border border-[var(--border)] bg-[var(--surface)] p-8 text-center">
          <MessageCircle className="w-10 h-10 mx-auto text-[var(--accent)] mb-3" />
          <p className="text-sm text-[var(--text-dim)] mb-4 max-w-md mx-auto">
            Activa tu asistente público. Se creará un enlace propio para compartir con tus clientes.
          </p>
          <Button onClick={() => save({ enabled: true })} disabled={saving}
                  className="rounded-none bg-[var(--accent)] text-[var(--bg)] hover:bg-[var(--accent)]/80" data-testid="asi-activate">
            Activar mi asistente
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Configuración + enlace */}
          <div className="space-y-4">
            <div className="border border-[var(--border)] bg-[var(--surface)] p-5 space-y-4">
              <div className="flex items-center justify-between">
                <div className="label-mono">TU ENLACE PÚBLICO</div>
                <div className="flex items-center gap-2">
                  <span className="label-mono" style={{ color: active ? "var(--accent)" : "var(--text-dim)" }}>
                    {active ? "ACTIVO" : "PAUSADO"}
                  </span>
                  <Switch checked={active} onCheckedChange={(v) => save({ enabled: v })} data-testid="asi-toggle" />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <code className="flex-1 font-mono text-xs bg-[var(--bg)] border border-[var(--border)] px-3 py-2 break-all">{url}</code>
                <Button variant="outline" className="rounded-none" onClick={() => copy(url)}><Copy className="w-3.5 h-3.5" /></Button>
                <a href={url} target="_blank" rel="noreferrer" className="p-2 border border-[var(--border)] text-[var(--text-dim)] hover:text-[var(--accent)]"><ExternalLink className="w-3.5 h-3.5" /></a>
              </div>
              <div>
                <Label className="label-mono">Para incrustar en tu web</Label>
                <div className="flex items-center gap-2 mt-1">
                  <code className="flex-1 font-mono text-[10px] bg-[var(--bg)] border border-[var(--border)] px-3 py-2 break-all">{embed}</code>
                  <Button variant="outline" className="rounded-none" onClick={() => copy(embed)}><Copy className="w-3.5 h-3.5" /></Button>
                </div>
              </div>
            </div>

            <div className="border border-[var(--accent)]/50 bg-[var(--surface)] p-5 space-y-3">
              <div className="label-mono text-[var(--accent)] flex items-center gap-2"><Wand2 className="w-3.5 h-3.5" /> AUTORRELLENAR DESDE TU WEB</div>
              <p className="text-xs text-[var(--text-dim)]">Pega la dirección de tu web y la IA rellena tu asistente (nombre, servicios, horarios, saludo) en segundos.</p>
              <div className="flex gap-2">
                <Input value={autoUrl} onChange={(e) => setAutoUrl(e.target.value)}
                       onKeyDown={(e) => e.key === "Enter" && autofill()}
                       placeholder="www.tunegocio.com" className="rounded-none bg-[var(--bg)] border-[var(--border)]" data-testid="asi-autourl" />
                <Button onClick={autofill} disabled={autofilling}
                        className="rounded-none bg-[var(--accent)] text-[var(--bg)] hover:bg-[var(--accent)]/80 whitespace-nowrap" data-testid="asi-autofill">
                  <Wand2 className="w-3.5 h-3.5 mr-1" /> {autofilling ? "Leyendo…" : "Leer mi web"}
                </Button>
              </div>
            </div>

            <div className="border border-[var(--border)] bg-[var(--surface)] p-5 space-y-3">
              <div className="label-mono">PERSONALIZA</div>
              <div>
                <Label className="label-mono">Nombre del negocio</Label>
                <Input value={cfg.business_name || ""} onChange={(e) => set("business_name", e.target.value)}
                       className="rounded-none bg-[var(--bg)] border-[var(--border)]" />
              </div>
              <div>
                <Label className="label-mono">Mensaje de bienvenida</Label>
                <Textarea value={cfg.welcome || ""} onChange={(e) => set("welcome", e.target.value)}
                          placeholder="¡Hola! ¿En qué te ayudo?" rows={2}
                          className="rounded-none bg-[var(--bg)] border-[var(--border)]" />
              </div>
              <div>
                <Label className="label-mono">Qué es tu negocio</Label>
                <Textarea value={cfg.descripcion || ""} onChange={(e) => set("descripcion", e.target.value)} rows={2}
                          placeholder="Breve descripción de tu negocio" className="rounded-none bg-[var(--bg)] border-[var(--border)]" />
              </div>
              <div>
                <Label className="label-mono">Servicios / productos</Label>
                <Textarea value={cfg.servicios || ""} onChange={(e) => set("servicios", e.target.value)} rows={2}
                          placeholder="Lo que ofreces" className="rounded-none bg-[var(--bg)] border-[var(--border)]" />
              </div>
              <div>
                <Label className="label-mono">Horarios</Label>
                <Input value={cfg.horarios || ""} onChange={(e) => set("horarios", e.target.value)}
                       placeholder="L-V 9:00-20:00…" className="rounded-none bg-[var(--bg)] border-[var(--border)]" />
              </div>
              <div className="flex items-center justify-between">
                <Label className="label-mono">Permitir reservas / citas</Label>
                <Switch checked={cfg.booking_enabled !== false} onCheckedChange={(v) => set("booking_enabled", v)} />
              </div>
              <div>
                <Label className="label-mono">Etiqueta del detalle</Label>
                <Input value={cfg.booking_label || ""} onChange={(e) => set("booking_label", e.target.value)}
                       placeholder="Nº de personas / Servicio" className="rounded-none bg-[var(--bg)] border-[var(--border)]" />
              </div>
              <div>
                <Label className="label-mono">Avisarme al recibir una reserva (vía acción n8n)</Label>
                <select
                  value={cfg.notify_action_id || ""}
                  onChange={(e) => set("notify_action_id", e.target.value)}
                  className="w-full rounded-none bg-[var(--bg)] border border-[var(--border)] px-3 py-2 text-sm text-white outline-none focus:border-[var(--accent)]"
                  data-testid="asi-notify"
                >
                  <option value="">— Ninguno —</option>
                  {acts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
                <div className="text-[10px] text-[var(--text-dim)] font-mono mt-1">
                  Conecta primero tu WhatsApp en <strong>Acciones (n8n)</strong> y elígelo aquí: cada reserva te llegará al móvil.
                </div>
              </div>
              <Button onClick={() => save()} disabled={saving}
                      className="rounded-none bg-[var(--accent)] text-[var(--bg)] hover:bg-[var(--accent)]/80" data-testid="asi-save">
                Guardar cambios
              </Button>
            </div>
          </div>

          {/* Reservas recibidas */}
          <div className="border border-[var(--border)] bg-[var(--surface)]">
            <div className="p-4 border-b border-[var(--border)] label-mono flex items-center gap-2">
              <CalendarClock className="w-3.5 h-3.5" /> RESERVAS / CITAS ({resv.length})
            </div>
            {resv.length === 0 ? (
              <div className="p-8 text-center text-sm text-[var(--text-dim)] font-mono">Aún no hay reservas.</div>
            ) : (
              <div className="max-h-[560px] overflow-auto">
                {resv.map((r) => (
                  <div key={r.id} className="p-4 border-b border-[var(--border)]" data-testid={`resv-${r.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="text-sm font-semibold text-white">{r.name}</div>
                        <div className="label-mono mt-0.5">{r.contact}</div>
                        <div className="text-xs text-[var(--text-dim)] mt-1">
                          {[r.date, r.time, r.detail].filter(Boolean).join(" · ")}
                        </div>
                        {r.notes && <div className="text-xs text-[var(--text-dim)] mt-1 italic">"{r.notes}"</div>}
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <span className="label-mono text-[10px]" style={{
                          color: r.status === "confirmed" ? "var(--accent)" : r.status === "cancelled" ? "var(--danger)" : "var(--warn)"
                        }}>
                          {r.status === "confirmed" ? "CONFIRMADA" : r.status === "cancelled" ? "CANCELADA" : "PENDIENTE"}
                        </span>
                        {r.status !== "confirmed" && (
                          <button onClick={() => setStatus(r.id, "confirmed")} className="text-[var(--accent)] hover:opacity-70" title="Confirmar"><Check className="w-4 h-4" /></button>
                        )}
                        {r.status !== "cancelled" && (
                          <button onClick={() => setStatus(r.id, "cancelled")} className="text-[var(--danger)] hover:opacity-70" title="Cancelar"><X className="w-4 h-4" /></button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
